import { tracked } from "@glimmer/tracking";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import { schedule } from "@ember/runloop";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import getURL from "discourse/lib/get-url";
import DiscourseURL from "discourse/lib/url";
import { i18n } from "discourse-i18n";
import {
  COMPOSER_CLOSED_MESSAGE,
  COMPOSER_OPENED_MESSAGE,
  isTrustedComposerMessage,
  OPEN_COMPOSER_MESSAGE,
  postComposerMessage,
} from "discourse/plugins/discourse-mod-market/discourse/lib/mod-market-composer-messages";

export default class ModMarketShowController extends Controller {
  @service a11y;
  @service currentUser;

  @tracked tab = "description";
  @tracked version = null;
  @tracked favorited = false;
  @tracked favoriteCount = 0;
  @tracked favoriteSaving = false;
  @tracked commentComposerOpen = false;
  @tracked detailsExpanded = false;

  queryParams = ["tab", "version"];
  #discussionFrame = null;
  #composerMessageListenerActive = false;

  get availableTabs() {
    return ["description", "changelog", "comments"];
  }

  get activeTab() {
    return this.availableTabs.includes(this.tab) ? this.tab : "description";
  }

  get availableVersions() {
    return this.model.versions || [];
  }

  get selectedVersion() {
    return (
      this.availableVersions.find((item) => item.version === this.version) ||
      this.availableVersions.find((item) => item.is_latest) ||
      this.availableVersions[0]
    );
  }

  get favoriteDisabled() {
    return Boolean(this.currentUser && !this.model.capabilities?.can_favorite);
  }

  get favoriteUnavailableMessageKey() {
    switch (this.model.capabilities?.favorite_unavailable_reason) {
      case "missing_external_identity":
        return "discourse_mod_market.favorite.not_linked";
      case "identity_unavailable":
        return "discourse_mod_market.favorite.identity_unavailable";
      default:
        return "discourse_mod_market.favorite.unavailable";
    }
  }

  get discussionUrl() {
    if (!this.model.topic_id) {
      return null;
    }

    return getURL(
      `/embed/comments?topic_id=${this.model.topic_id}&full_app=1&class_name=mod-market-discussion`
    );
  }

  get selectedDescriptionCooked() {
    return this.selectedVersion?.description_cooked;
  }

  get selectedCompatibility() {
    return (
      this.selectedVersion?.requirement ||
      [this.selectedVersion?.loader, this.selectedVersion?.game_version]
        .filter(Boolean)
        .join(" · ") ||
      this.model.compatible_game_version
    );
  }

  get showCommentEntry() {
    return (
      this.activeTab !== "comments" ||
      Boolean(this.discussionUrl && !this.commentComposerOpen)
    );
  }

  get emptyContentMessageKey() {
    return `discourse_mod_market.detail.empty.${this.activeTab}`;
  }

  @action
  goBack(event) {
    const modified =
      event &&
      (event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button);
    if (modified) {
      return;
    }
    event?.preventDefault?.();

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    DiscourseURL.routeTo(getURL("/mods"));
  }

  @action
  selectTab(tab) {
    if (tab !== "comments") {
      this.commentComposerOpen = false;
    }
    this.tab = tab;
  }

  @action
  toggleDetailsExpanded() {
    this.detailsExpanded = !this.detailsExpanded;
  }

  @action
  openCommentComposer() {
    if (!this.currentUser) {
      getOwner(this).lookup("route:application").send("showLogin");
      return;
    }

    this.commentComposerOpen = Boolean(this.discussionUrl);
    this.tab = "comments";

    if (this.commentComposerOpen) {
      schedule("afterRender", () => this.#requestCommentComposer());
    }
  }

  @action
  registerDiscussionFrame(element) {
    this.#discussionFrame = element;
    this.#requestCommentComposer();
  }

  @action
  unregisterDiscussionFrame(element) {
    if (this.#discussionFrame === element) {
      this.#discussionFrame = null;
    }
  }

  @action
  discussionFrameLoaded(event) {
    if (event.currentTarget === this.#discussionFrame) {
      this.#requestCommentComposer();
    }
  }

  setupComposerMessaging() {
    if (this.#composerMessageListenerActive) {
      return;
    }

    window.addEventListener("message", this.handleComposerOpenedMessage);
    window.addEventListener("message", this.handleComposerClosedMessage);
    this.#composerMessageListenerActive = true;
  }

  teardownComposerMessaging() {
    if (!this.#composerMessageListenerActive) {
      return;
    }

    window.removeEventListener("message", this.handleComposerOpenedMessage);
    window.removeEventListener("message", this.handleComposerClosedMessage);
    this.#composerMessageListenerActive = false;
    this.#discussionFrame = null;
  }

  @action
  handleComposerOpenedMessage(event) {
    if (
      !isTrustedComposerMessage(event, {
        type: COMPOSER_OPENED_MESSAGE,
        source: this.#discussionFrame?.contentWindow,
      })
    ) {
      return;
    }

    this.commentComposerOpen = true;
  }

  @action
  handleComposerClosedMessage(event) {
    if (
      !isTrustedComposerMessage(event, {
        type: COMPOSER_CLOSED_MESSAGE,
        source: this.#discussionFrame?.contentWindow,
      })
    ) {
      return;
    }

    this.commentComposerOpen = false;
  }

  #requestCommentComposer() {
    if (!this.commentComposerOpen) {
      return;
    }

    postComposerMessage(
      this.#discussionFrame?.contentWindow,
      OPEN_COMPOSER_MESSAGE
    );
  }

  @action
  selectVersion(version) {
    this.version = version;
    this.a11y.announce(
      i18n("discourse_mod_market.detail.version_selected", {
        version: this.version,
      }),
      "polite"
    );
  }

  @action
  scrollVersions(event) {
    const scroller = event.currentTarget.previousElementSibling;
    if (!scroller) {
      return;
    }

    const direction = getComputedStyle(scroller).direction === "rtl" ? -1 : 1;
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    scroller.scrollBy({
      left: direction * 128,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }

  @action
  navigateTabs(tab, event) {
    const currentIndex = this.availableTabs.indexOf(tab);
    let nextTab;
    switch (event.key) {
      case "ArrowLeft":
        nextTab =
          this.availableTabs[
            (currentIndex - 1 + this.availableTabs.length) %
              this.availableTabs.length
          ];
        break;
      case "ArrowRight":
        nextTab =
          this.availableTabs[(currentIndex + 1) % this.availableTabs.length];
        break;
      case "Home":
        nextTab = this.availableTabs[0];
        break;
      case "End":
        nextTab = this.availableTabs.at(-1);
        break;
      default:
        return;
    }

    event.preventDefault();
    event.currentTarget.parentElement
      .querySelector(`[data-mod-market-tab="${nextTab}"]`)
      ?.focus();
    this.selectTab(nextTab);
  }

  @action
  async toggleFavorite() {
    if (!this.currentUser) {
      getOwner(this).lookup("route:application").send("showLogin");
      return;
    }

    this.favoriteSaving = true;
    try {
      if (this.favorited) {
        await ajax(`/mod-market-api/v1/mods/${this.model.id}/favorite`, {
          type: "DELETE",
        });
        this.favorited = false;
        this.favoriteCount = Math.max(0, this.favoriteCount - 1);
      } else {
        await ajax(`/mod-market-api/v1/mods/${this.model.id}/favorite`, {
          type: "PUT",
        });
        this.favorited = true;
        this.favoriteCount += 1;
      }
      this.a11y.announce(
        i18n(
          this.favorited
            ? "discourse_mod_market.favorite.added"
            : "discourse_mod_market.favorite.removed"
        ),
        "polite"
      );
    } catch (error) {
      popupAjaxError(error);
    } finally {
      this.favoriteSaving = false;
    }
  }
}
